import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { authAPI, setAuthToken } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const AuthContext = createContext(null);
axios.defaults.withCredentials = true;

const formatError = (detail, fallback) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || String(item)).join(' ');
  return fallback;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch (error) { /* local logout still proceeds */ }
    setAuthToken(null);
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      setAuthToken(null);
      setToken(null);
      setUser(null);
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const response = await authAPI.me();
        setUser(response.data);
      } catch (error) {
        setUser(null);
      } finally { setLoading(false); }
    };
    restoreSession();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { access_token: accessToken, user: userData } = response.data;
      if (!accessToken) return { success: false, error: 'Invalid server response' };
      setAuthToken(accessToken);
      setToken(accessToken);
      setUser(userData);
      return { success: true };
    } catch (error) {
      const detail = formatError(error.response?.data?.detail, 'Login failed');
      if (error.response?.status === 429) return { success: false, error: detail };
      if (error.response?.status === 401) return { success: false, error: 'Invalid email or password' };
      return { success: false, error: detail };
    }
  };

  const register = async (email, password, name, role = 'user') => {
    try {
      const response = await authAPI.register({ email, password, name, role });
      if (response.data.access_token) {
        setAuthToken(response.data.access_token);
        setToken(response.data.access_token);
        setUser(response.data.user);
      }
      return { success: true, status: response.data.status || response.data.user?.status || 'pending', message: response.data.message };
    } catch (error) {
      return { success: false, error: formatError(error.response?.data?.detail, 'Registration failed') };
    }
  };

  useEffect(() => {
    if (!user) return undefined;
    const heartbeat = () => fetch(`${API_URL}/sessions/heartbeat`, {
      method: 'POST', credentials: 'include', headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ current_page: window.location.pathname }), keepalive: true,
    }).catch(() => {});
    heartbeat();
    const interval = setInterval(heartbeat, 60000);
    return () => clearInterval(interval);
  }, [user, token]);

  return <AuthContext.Provider value={{
    user, token, loading, login, register, logout,
    isAdmin: () => user?.role === 'admin',
    isManager: () => user?.role === 'manager' || user?.role === 'admin',
    isUser: () => Boolean(user), setUser,
  }}>{children}</AuthContext.Provider>;
};